import React from 'react';
import {
  Box,
  Typography,
  Alert,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
  Link,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import {
  disciplineBadge,
  disciplineDisplayName,
  GEN_COND_FULL_NAME,
} from './discipline-badge';
import ChatToolPicker, { CHAT_TOOL_LABEL_BY_ID } from './ChatToolPicker';
import AssistantMarkdown from './AssistantMarkdown';

// Internal-only sentinel for the MUI Select's own `value`/MenuItem `value`
// props -- MUI's Select does not reliably round-trip a literal `null`
// through its controlled-value comparison. Translated to/from the real
// wire value (`null` = project-wide/"GEN COND") at this component's own
// boundary only; onScopeChange always receives/is called with the real
// `null` or discipline UUID, never this sentinel.
const SCOPE_PROJECT_WIDE_VALUE = '__project_wide__';
const SCOPE_SELECTOR_LABEL_ID = 'assistant-scope-label';

// Standard "visually hidden" (sr-only) technique: present in the a11y tree
// (so the Select's accessible name is still "Scope", not a floating visible
// InputLabel taking up horizontal space next to a control that is meant to
// read as a small, chip-like control). See MUI's own `visuallyHidden` sx
// helper for the same set of properties.
const VISUALLY_HIDDEN_SX = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

// Fixed, safe copy for the two non-"succeeded" RAG outcomes. 'succeeded'
// renders the answer text normally with no extra banner.
const RAG_STATUS_BANNER = {
  no_current_sources:
    'No current project documents were found to answer this question.',
  blocked: 'This question could not be answered.',
};

/**
 * Renders one already-condensed citation (services/ai/project-docs/
 * citation-condensation.js's output: one entry per cited document, with a
 * precomputed pageLabel -- "p. 1" / "pp. 1–3" / "pp. 1, 3–4, 7", or no
 * label at all when the document carried no page metadata) -- file name,
 * optional page label, optional section title, as plain text nodes only.
 * Model output and citation fields are untrusted content: never rendered
 * as HTML. This component performs no grouping/condensation of its own --
 * it only presents what it is given.
 *
 * Clickable when `onOpen` is supplied: opens the SAME cited document/
 * version via the authenticated file endpoint (see
 * ProjectDocumentsAssistantPage.js's handleOpenCitation), deep-linked to
 * its first cited page when the citation carried one. citation.documentId/
 * documentVersion are used only as the navigation KEY passed to `onOpen` --
 * this component never renders either value, internal ids are never
 * displayed.
 */
const CitationChip = ({ citation, onOpen }) => {
  const parts = [citation.fileName];
  if (citation.pageLabel) {
    parts.push(citation.pageLabel);
  }
  if (citation.sectionTitle) {
    parts.push(citation.sectionTitle);
  }
  const clickable = Boolean(
    onOpen && citation.documentId && citation.documentVersion
  );
  return (
    <Chip
      size="small"
      label={parts.join(' — ')}
      clickable={clickable}
      onClick={clickable ? () => onOpen(citation) : undefined}
      sx={{
        height: 22,
        fontSize: 11,
        fontWeight: 500,
        bgcolor: '#e8f0fe',
        color: '#1a56c4',
        '& .MuiChip-label': { px: 1.1 },
      }}
    />
  );
};

// The one remaining place either schedule type's display label lives on the
// frontend (schedule-tool-config.js and ScheduleRunDialog.js, the only other
// consumers of a per-type label, were removed under the final product
// direction -- Schedule Tools is chat-only now, see this file's own
// ToolResultCard docstring).
const TOOL_RESULT_LABELS = {
  piping: 'Piping Schedule',
  ductwork: 'Ductwork Schedule',
  electrical_material: 'Electrical Material Schedule',
};

/**
 * Renders the downloadable-artifact action for a Schedule-Tool-answered
 * assistant message (Migration 076's `toolResult` -- see
 * services/ai/project-docs/chat-schedule-tool-dispatch.js). The chat message's own
 * `content` text already carries the concise human-readable outcome (e.g.
 * "Piping Schedule created successfully. 52 requirements..." or "...adding
 * it to Project Documents failed: ..."); this card ONLY adds the download
 * action -- it never repeats or reformats that text.
 *
 * FINAL PRODUCT DIRECTION: Download is the ONE artifact action here, never
 * a generic "Open" action or a schedule-management dialog of any kind (see
 * ProjectDocumentsAssistantPage.js's own module docstring: the schedule
 * feature is a chat capability, and "ask in chat -> get schedule ->
 * download it" is the whole intended path). The label names the exact
 * artifact ("Download Piping Schedule" / "Download Ductwork Schedule"),
 * never a bare "Download", so it's obvious what file the action represents.
 * The server-side conversational `open`/`show` intent still exists and is
 * still answered in chat text (see chat-schedule-tool-dispatch.js's own
 * describeExistingRun) -- an "opened" toolResult still has a real runId, so
 * this same download action appears under that response too.
 *
 * Omitted entirely when there is no real saved run to act on (`runId ===
 * null`, e.g. "open the piping schedule" before one has ever been
 * generated, or "no eligible specification sources were found") -- never
 * rendered as a disabled/broken action.
 */
const ToolResultCard = ({ toolResult, onDownload }) => {
  if (!toolResult || !toolResult.runId) {
    return null;
  }
  const label = TOOL_RESULT_LABELS[toolResult.tool] || 'Schedule';
  return (
    <Box
      sx={{
        mt: 1.25,
        p: 1,
        borderRadius: 1,
        border: '1px solid #e2e5ea',
        bgcolor: '#fafbfc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
      }}
    >
      <Link
        component="button"
        type="button"
        variant="caption"
        underline="always"
        onClick={() => onDownload(toolResult)}
        sx={{ fontWeight: 600 }}
      >
        {`Download ${label}`}
      </Link>
    </Box>
  );
};

const Avatar = ({ isUser, initials }) => (
  <Box
    sx={{
      width: 26,
      height: 26,
      borderRadius: '50%',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 11,
      fontWeight: isUser ? 400 : 600,
      bgcolor: isUser ? '#eef0f3' : '#e8f0fe',
      color: isUser ? '#5c6478' : '#1a56c4',
    }}
  >
    {initials}
  </Box>
);

const MessageBubble = ({
  message,
  userInitials,
  onDownloadToolResult,
  onOpenCitation,
}) => {
  const isUser = message.role === 'user';
  const banner =
    !isUser && message.ragStatus ? RAG_STATUS_BANNER[message.ragStatus] : null;

  return (
    <Box sx={{ display: 'flex', gap: 1.25, mb: 1.75 }}>
      <Avatar isUser={isUser} initials={isUser ? userInitials : 'H'} />
      <Box
        sx={{
          maxWidth: '80%',
          px: 1.6,
          py: 1.25,
          borderRadius: 2,
          fontSize: 13,
          color: '#1a2233',
          bgcolor: isUser ? '#f5f6f8' : '#fff',
          border: isUser ? 'none' : '1px solid #e2e5ea',
        }}
      >
        {/* Untrusted content, never HTML: the user's own text stays plain;
            Herb's answer renders as sanitized Markdown (AssistantMarkdown). */}
        {isUser ? (
          <Typography
            variant="body2"
            sx={{ whiteSpace: 'pre-wrap', fontSize: 13 }}
          >
            {message.content}
          </Typography>
        ) : (
          <AssistantMarkdown content={message.content} />
        )}

        {banner && (
          <Alert severity="info" variant="outlined" sx={{ mt: 1.5 }}>
            {banner}
          </Alert>
        )}

        {!isUser &&
          Array.isArray(message.citations) &&
          message.citations.length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {message.citations.map((citation) => (
                <CitationChip
                  key={citation.documentId}
                  citation={citation}
                  onOpen={onOpenCitation}
                />
              ))}
            </Box>
          )}

        {!isUser && (
          <ToolResultCard
            toolResult={message.toolResult}
            onDownload={onDownloadToolResult}
          />
        )}
      </Box>
    </Box>
  );
};

/**
 * A selected tool alone (blank typed text) displays as just that tool's own
 * authoritative label ("Piping Schedule") -- never blank. A selected tool
 * PLUS typed text displays both, tool label bracketed ("[Piping Schedule]
 * Remove this column") so the user can see exactly what was selected
 * alongside what they typed, without either piece being lost. Ordinary
 * typed text with no tool selected displays exactly as typed, unchanged.
 * This is a DISPLAY-only composition of the two already-structured pieces
 * (`pendingTurn.tool`/`pendingTurn.question`) -- it is never fed back into
 * detectScheduleToolIntent or persisted as the literal question text (see
 * assistant-controller.js's own askQuestion docstring for that boundary).
 */
const pendingTurnDisplayText = ({ question, tool }) => {
  const toolLabel = tool ? CHAT_TOOL_LABEL_BY_ID[tool] || tool : null;
  if (!toolLabel) {
    return question || '';
  }
  return question ? `[${toolLabel}] ${question}` : toolLabel;
};

/**
 * Renders the ONE outstanding UI-only optimistic question turn (see
 * ProjectDocumentsAssistantPage.js's own pendingTurn docstring) -- never a
 * real persisted message, never given a `messages`-array key/id. Reuses the
 * exact same user-bubble visual treatment as MessageBubble so the
 * optimistic bubble and the eventual authoritative one are visually
 * identical once reconciled. Two states only:
 *   'pending' -- a small working indicator, no Retry action.
 *   'failed'  -- the bubble is visually flagged Failed, with a Retry
 *     action that resends this EXACT question (never creates a second
 *     visible copy of it).
 */
const PendingTurnBubble = ({ pendingTurn, userInitials, onRetry }) => {
  const failed = pendingTurn.status === 'failed';
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1.75 }}>
      <Box sx={{ display: 'flex', gap: 1.25 }}>
        <Avatar isUser initials={userInitials} />
        <Box
          sx={{
            maxWidth: '80%',
            px: 1.6,
            py: 1.25,
            borderRadius: 2,
            fontSize: 13,
            color: '#1a2233',
            bgcolor: failed ? '#fdecea' : '#f5f6f8',
            border: failed ? '1px solid #f3b4ae' : 'none',
          }}
        >
          <Typography
            variant="body2"
            sx={{ whiteSpace: 'pre-wrap', fontSize: 13 }}
          >
            {pendingTurnDisplayText(pendingTurn)}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pl: 4.75,
          mt: 0.5,
        }}
      >
        {failed ? (
          <>
            <Typography
              variant="caption"
              color="error"
              sx={{ fontWeight: 600 }}
            >
              Failed to send.
            </Typography>
            <Link
              component="button"
              type="button"
              variant="caption"
              underline="always"
              onClick={() => onRetry(pendingTurn.localId)}
              sx={{ fontWeight: 600 }}
            >
              Retry
            </Link>
          </>
        ) : (
          <>
            <CircularProgress size={12} />
            <Typography variant="caption" color="text.secondary">
              Herb is thinking…
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

const userInitialsFrom = (auth) => {
  const first = (auth.userFirstName || '').trim();
  const last = (auth.userLastName || '').trim();
  const initials = `${first[0] || ''}${last[0] || ''}`.toUpperCase();
  return initials || '?';
};

/**
 * Compact, chip-like scope selector rendered directly adjacent to the
 * composer -- applies to either the current draft (conversationId === '',
 * edits draftDisciplineId directly, no request) or an existing conversation
 * (edits its persisted search scope via onScopeChange, which the parent
 * wires to handleScopeChange -- an async PATCH). Options are always GEN
 * COND (pinned first, the synthetic project-wide entry) followed by every
 * active discipline EXCEPT a real "General Conditions" discipline row
 * (excluded upstream by the parent, by name, to avoid two same-labeled
 * choices -- see ProjectDocumentsAssistantPage.js).
 *
 * The CLOSED control renders as a small colored chip -- the color is the
 * SAME deterministic id-hashed derivation as ConversationSidebar's own
 * per-conversation badge (see discipline-badge.js's disciplineBadge), never
 * a second, independently-tuned color scheme. The LABEL, however, is always
 * the discipline's FULL name (via disciplineDisplayName), never the short
 * abbreviation ConversationSidebar's history badge uses -- the composer is
 * a live, editable control the user reads more closely than a history list,
 * so it favors clarity over compactness. The OPEN dropdown list shows the
 * same full names (MenuItem text) for both the synthetic project-wide entry
 * and every real discipline.
 */
const ScopeSelector = ({
  value,
  disciplines,
  disciplinesLoading,
  disabled,
  onChange,
}) => {
  const selectValue =
    value === null
      ? SCOPE_PROJECT_WIDE_VALUE
      : value === undefined
        ? ''
        : value;
  // disciplineBadge/disciplineDisplayName both fail closed on `undefined`
  // (a missing/malformed scope -- see ProjectDocumentsAssistantPage.js's
  // displayedSelectorScope) to a distinct unknown/invalid presentation,
  // never General Conditions -- this component does not re-implement that
  // distinction, it only renders whatever the shared helpers resolve.
  // `badge` supplies only the chip's COLOR here; its `label` (a short
  // abbreviation) is never rendered by the composer -- see displayName.
  const badge = disciplineBadge(value, disciplines);
  const displayName = disciplineDisplayName(value, disciplines);

  return (
    <FormControl
      size="small"
      disabled={disabled}
      // flexShrink: 0 (replacing the previous minWidth: 0) -- this
      // FormControl sits directly in the composer's flex row next to a
      // `fullWidth` TextField (see ChatPanel below). `minWidth: 0` opted
      // this pill INTO shrinking below its own content's natural width
      // whenever the row was too narrow to fit both at their natural
      // sizes, which is what let "General Conditions" (the longest label)
      // get visually compressed into overlapping the caret -- no amount of
      // internal padding can fix an overlap caused by the OUTER box being
      // squeezed narrower than its own content needs. flexShrink: 0 keeps
      // this pill at its natural, content-driven width always; the
      // TextField (which handles a shrunk width gracefully, unlike a
      // rigid pill+icon) absorbs any width pressure instead.
      sx={{ flexShrink: 0 }}
    >
      <Box
        component="span"
        id={SCOPE_SELECTOR_LABEL_ID}
        sx={VISUALLY_HIDDEN_SX}
      >
        Scope
      </Box>
      <Select
        labelId={SCOPE_SELECTOR_LABEL_ID}
        value={selectValue}
        displayEmpty
        onChange={(e) => {
          const raw = e.target.value;
          onChange(raw === SCOPE_PROJECT_WIDE_VALUE ? null : raw);
        }}
        renderValue={() => displayName}
        sx={{
          height: 26,
          borderRadius: 999,
          bgcolor: badge.color,
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            py: 0,
            pl: 1.25,
            // Reserves room for the dropdown caret so it never renders on
            // top of the label text. Labels here are full discipline names
            // (see disciplineDisplayName) and can be materially longer than
            // ConversationSidebar's short badges.
            pr: 5,
            // Never wrap to a second line -- this pill has a fixed,
            // vertically-centered height (26px); wrapped text would clip
            // rather than grow. Paired with flexShrink: 0 on the enclosing
            // FormControl above (the actual fix for the caret-overlap
            // regression -- see that comment), this guarantees every label
            // renders on one line at its natural width, with pr's clearance
            // actually honored instead of being squeezed away.
            whiteSpace: 'nowrap',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.3,
            color: '#fff',
          },
          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
          '& .MuiSelect-icon': {
            color: '#fff',
            // Pinned close to the pill's outer edge and shrunk down --
            // stays entirely inside the padding reserved above rather than
            // MUI's default icon position/size, which overlapped GEN COND.
            right: 6,
            fontSize: 18,
          },
        }}
      >
        <MenuItem value={SCOPE_PROJECT_WIDE_VALUE}>
          {GEN_COND_FULL_NAME}
        </MenuItem>
        {disciplines.map((discipline) => (
          <MenuItem key={discipline.id} value={discipline.id}>
            {discipline.name}
          </MenuItem>
        ))}
      </Select>
      {disciplinesLoading && (
        <CircularProgress
          size={12}
          sx={{ position: 'absolute', right: -18, top: 7 }}
        />
      )}
    </FormControl>
  );
};

/**
 * Center pane: message history for the selected conversation (or the
 * not-yet-persisted draft, conversationId === ''), plus the composer. The
 * composer is enabled as soon as a project is selected -- it is never
 * gated on a conversation already existing; sending from a fresh draft is
 * what lazily creates the conversation (see
 * ProjectDocumentsAssistantPage.js's sendDraftFirstTurn). Citations are
 * rendered inline on the assistant message they belong to (see
 * MessageBubble above) -- never as a separate list.
 */
const ChatPanel = ({
  auth,
  conversationId,
  messages,
  messagesLoading,
  pendingTurn,
  question,
  submitting,
  onQuestionChange,
  onAskQuestion,
  onRetryQuestion,
  scopeValue,
  disciplines,
  disciplinesLoading,
  scopeDisabled,
  onScopeChange,
  scopeUpdateError,
  onDownloadToolResult,
  onOpenCitation,
  sourceOpenError,
  scheduleDownloadError,
  selectedTool,
  onSelectTool,
  onClearTool,
}) => {
  const userInitials = userInitialsFrom(auth);
  // Matches the currently-displayed view (draft or a real conversation) to
  // the pendingTurn snapshot it belongs to. A still-uncreated draft's
  // pendingTurn.conversationId is null while conversationId itself is '' --
  // normalizing null to '' lets one comparison cover both the draft and the
  // real-conversation cases.
  const showPendingTurn =
    pendingTurn && (pendingTurn.conversationId || '') === conversationId;

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        // Required alongside overflow:hidden/auto lower in this same
        // nested flex chain -- without it, a flex item that is ITSELF a
        // flex container (this Box lays out [scrollable messages, fixed
        // composer] in a column) can be pushed taller than the space its
        // ROW parent (the 3-pane Box in ProjectDocumentsAssistantPage.js)
        // actually allotted it, once real chat content grows past a
        // trivial size -- pushing the WHOLE PAGE taller than the viewport
        // and causing body-level scroll, which is what let the pinned
        // Conversations/Documents side panes scroll away instead of
        // staying visible. Every other level of this exact
        // flex:1+minHeight:0+overflow chain already sets this (the 3-pane
        // row Box, and this component's own inner messages Box below) --
        // this was the one missing link.
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        p: 2.5,
        gap: 1.5,
        bgcolor: '#fff',
      }}
    >
      <Box
        data-testid="chat-messages-scroll-region"
        sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}
      >
        {messagesLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!messagesLoading && messages.length === 0 && !showPendingTurn && (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Hi, I&apos;m Herb. Ask a question about this project&apos;s
              documents below.
            </Typography>
          </Box>
        )}

        {!messagesLoading &&
          messages.map((message) => (
            <MessageBubble
              key={message.id}
              message={message}
              userInitials={userInitials}
              onDownloadToolResult={onDownloadToolResult}
              onOpenCitation={onOpenCitation}
            />
          ))}

        {!messagesLoading && showPendingTurn && (
          <PendingTurnBubble
            pendingTurn={pendingTurn}
            userInitials={userInitials}
            onRetry={onRetryQuestion}
          />
        )}
      </Box>

      {scopeUpdateError && (
        <Typography variant="caption" color="error">
          {scopeUpdateError}
        </Typography>
      )}

      {sourceOpenError && (
        <Typography variant="caption" color="error">
          {sourceOpenError}
        </Typography>
      )}

      {scheduleDownloadError && (
        <Typography variant="caption" color="error">
          {scheduleDownloadError}
        </Typography>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ScopeSelector
          value={scopeValue}
          disciplines={disciplines}
          disciplinesLoading={disciplinesLoading}
          disabled={scopeDisabled}
          onChange={onScopeChange}
        />
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={4}
          placeholder="Ask about specs, drawings, or submittals for this project…"
          value={question}
          disabled={submitting}
          onChange={(e) => onQuestionChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onAskQuestion();
            }
          }}
          InputProps={{
            'data-testid': 'chat-composer-input',
            // pl: MUI's own OutlinedInput root default left padding (14px)
            // is meant for plain text with no adornment -- with the tool
            // picker as startAdornment, that much inset reads as the "+"
            // floating toward the center rather than sitting near the
            // composer's own left edge. Reduced to a small, still-
            // comfortable inset (never 0 -- the control must not touch the
            // border).
            sx: { fontSize: 13, borderRadius: 1.5, pl: 0.75 },
            // The tool picker is the composer's own startAdornment -- the
            // SAME single bordered OutlinedInput as the text area and the
            // Send endAdornment below, never a separate external control
            // (see ChatToolPicker.js's own docstring for the plain "+" /
            // selected-tool Chip states it renders in this same slot).
            startAdornment: (
              <Box sx={{ mr: 0.5, display: 'flex' }}>
                <ChatToolPicker
                  selectedTool={selectedTool}
                  onSelect={onSelectTool}
                  onClear={onClearTool}
                  disabled={submitting}
                />
              </Box>
            ),
            endAdornment: (
              <IconButton
                size="small"
                color="primary"
                onClick={onAskQuestion}
                // A selected tool alone (blank typed text) is already a
                // complete, valid request -- see ChatToolPicker.js's own
                // docstring. Send is disabled only when there is NEITHER a
                // selected tool NOR typed text.
                disabled={submitting || (!question.trim() && !selectedTool)}
                aria-label="Send question"
              >
                {pendingTurn?.status === 'pending' ? (
                  <CircularProgress size={18} />
                ) : (
                  <SendIcon fontSize="small" />
                )}
              </IconButton>
            ),
          }}
        />
      </Box>
    </Box>
  );
};

export default ChatPanel;
